import * as React from 'react';

import classNames from 'classnames';

import ControlBar from 'bundles/general-video-player/components/control-bar/ControlBar';
import type { VideoPlayer as VideoPlayerType } from 'bundles/item-lecture';
import type { VideoPopup } from 'bundles/video-player/types/VideoPlayer';

/* eslint-enable no-restricted-imports */
import 'css!./__styles__/VideoControlsContainer';

type Props = {
  defaultSubtitleLanguage: string;
  player: VideoPlayerType;
  onChangeFullScreen: (isFullscreen: boolean) => void;
  isFullscreen: boolean;
  showToolbarAutoplayToggle?: boolean;
  isAutoPlayEnabled: boolean;
  onAutoPlayChange?: (isAutoPlayEnabled: boolean) => void;
  markers?: Array<number>;
  trimmedStartTimeSeconds: number;
  trimmedDurationSeconds: number | null;
};

type State = {
  hideContextMenu: boolean;
  isChromeVisible: boolean;
  visiblePopup: VideoPopup;
};

class VideoControlsContainer extends React.Component<Props, State> {
  declare node: HTMLElement | null | undefined;

  declare timeoutID: number | null | undefined;

  state = {
    isChromeVisible: false,
    visiblePopup: null,
    // disables the context menu on the container-level so
    // users can use the <video> element's context menu
    hideContextMenu: false,
  };

  componentDidMount() {
    const { player } = this.props;

    player.on('play', () => {
      this.timeoutID = window.setTimeout(() => {
        this.setChromeVisibility(false);
      }, 5000);
    });

    player.on('ended', () => {
      this.showChrome();
    });

    document.addEventListener('mousedown', this.handleDocumentMouseDown);
    document.addEventListener('mousemove', this.handleDocumentMouseMove);
  }

  componentWillUnmount() {
    const { player } = this.props;

    player.off('ended');

    if (this.timeoutID) {
      clearTimeout(this.timeoutID);
    }

    document.removeEventListener('mousedown', this.handleDocumentMouseDown);
    document.removeEventListener('mousemove', this.handleDocumentMouseMove);
  }

  handleDocumentMouseMove = () => {
    const { hideContextMenu } = this.state;

    if (hideContextMenu) {
      this.setState({
        hideContextMenu: false,
      });
    }
  };

  handleVideoRegionMouseMove = () => {
    if (this.timeoutID) {
      clearTimeout(this.timeoutID);
    }

    this.setChromeVisibility(true);
  };

  handleDocumentMouseDown = (event: MouseEvent) => {
    const { player } = this.props;
    const isFullscreen = player.isFullscreen();

    if (this.node && !this.node.contains(event.target as HTMLElement) && !isFullscreen) {
      if (!player.paused()) {
        this.setChromeVisibility(false);
      }
    }
  };

  applySubtitlePosition = ({ textTracks, line = 100, position = 50, align = 'bottom' }: $TSFixMe) => {
    textTracks.forEach(
      (track: $TSFixMe) =>
        track.cues &&
        Object.keys(track.cues).forEach((i) => {
          if (track.cues) {
            track.cues[i].position = position;
            track.cues[i].line = line;
            track.cues[i].align = align;
          }
        })
    );
  };

  onClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const { player } = this.props;

    if (player.paused()) {
      player.play();
    } else {
      player.pause();
    }
    player.on('play', () => {
      const textTracks = Array.from(player.textTracks());
      this.applySubtitlePosition({ textTracks });
    });
  };

  onShareClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    const { player } = this.props;

    if (!player.paused()) {
      player.pause();
    }
  };

  onContextMenu = () => {
    this.setState({
      hideContextMenu: true,
    });
  };

  hideChrome = (event?: React.FocusEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
    event?.stopPropagation();

    const { visiblePopup } = this.state;
    const { player } = this.props;
    const isFullscreen = player.isFullscreen();

    if (!player.paused() && !isFullscreen && visiblePopup === null) {
      this.setChromeVisibility(false);
    }
  };

  showChrome = (event?: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>) => {
    event?.stopPropagation();

    this.setChromeVisibility(true);
  };

  setChromeVisibility = (visible: boolean) => {
    const { visiblePopup } = this.state;

    this.setState({
      isChromeVisible: visible,
      visiblePopup: visible ? visiblePopup : null,
    });
  };

  setVisiblePopup = (popupName: VideoPopup) => {
    this.setState({
      visiblePopup: popupName,
    });
  };

  assignRef = (node: HTMLElement | null) => {
    this.node = node;
  };

  render() {
    const {
      defaultSubtitleLanguage,
      player,
      showToolbarAutoplayToggle,
      onChangeFullScreen,
      isAutoPlayEnabled,
      onAutoPlayChange,
      markers,
    } = this.props;

    const { isChromeVisible, visiblePopup, hideContextMenu } = this.state;

    return (
      <div
        ref={this.assignRef}
        role="presentation"
        className={classNames('rc-VideoControlsContainer', { hideContextMenu })}
        onClick={this.onClick}
        onFocus={this.showChrome}
        onContextMenu={this.onContextMenu}
        onBlur={this.hideChrome}
        onMouseEnter={this.showChrome}
        onMouseLeave={this.hideChrome}
        onMouseMove={this.handleVideoRegionMouseMove}
      >
        <ControlBar
          visiblePopup={visiblePopup}
          setVisiblePopup={this.setVisiblePopup}
          visible={isChromeVisible}
          player={player}
          markers={markers}
          defaultSubtitleLanguage={defaultSubtitleLanguage}
          showToolbarAutoplayToggle={showToolbarAutoplayToggle}
          onChangeFullScreen={onChangeFullScreen}
          isAutoPlayEnabled={isAutoPlayEnabled}
          onAutoPlayChange={onAutoPlayChange}
          trimmedStartTimeSeconds={this.props.trimmedStartTimeSeconds}
          trimmedDurationSeconds={this.props.trimmedDurationSeconds}
        />
      </div>
    );
  }
}

export default VideoControlsContainer;
